import { useEffect, useRef } from 'react';

export const useHeroNeonGrid = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    let lastFrameTime = 0;

    const updateGrid = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;

      const devicePixelRatio = Math.min(window.devicePixelRatio, 2);
      canvas.width = width * devicePixelRatio;
      canvas.height = height * devicePixelRatio;

      const ctx = canvas.getContext('2d');
      if (ctx) ctx.scale(devicePixelRatio, devicePixelRatio);

      const baseCols = width <= 500 ? 15 : 25;
      const baseRows = width <= 500 ? 10 : 15;

      const cols = baseCols;
      const rows = baseRows;

      const spacingX = width / cols;
      const spacingY = height / rows;

      const grid = [];
      for (let y = 0; y < rows; y++) {
        for (let x = 0; x < cols; x++) {
          grid.push({
            x: x * spacingX + spacingX / 2,
            y: y * spacingY + spacingY / 2,
            intensity: Math.random(),
          });
        }
      }

      return { grid, mouse: { x: width / 2, y: height / 2 } };
    };

    let { grid, mouse } = updateGrid();

    const draw = (time: number) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#0a0a0a';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const maxDistance = Math.min(canvas.width, canvas.height) / 10;
      grid.forEach((point) => {
        const pulse =
          Math.sin(time / 500 + point.intensity * Math.PI) * 0.5 + 0.5;
        const distToMouse = Math.hypot(mouse.x - point.x, mouse.y - point.y);
        const glowAmount = distToMouse < 100 ? 10 : 5;

        ctx.shadowColor = 'rgba(0, 200, 255, 0.8)';
        ctx.shadowBlur = glowAmount;

        ctx.beginPath();
        ctx.arc(point.x, point.y, pulse * 3, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0, 255, 255, ${pulse})`;
        ctx.fill();
        ctx.shadowBlur = 0;
      });

      grid.forEach((point) => {
        grid.forEach((otherPoint) => {
          const dist = Math.hypot(
            point.x - otherPoint.x,
            point.y - otherPoint.y
          );
          if (dist < maxDistance) {
            ctx.beginPath();
            ctx.moveTo(point.x, point.y);
            ctx.lineTo(otherPoint.x, otherPoint.y);
            ctx.strokeStyle = `rgba(0, 200, 255, ${1 - dist / maxDistance})`;
            ctx.lineWidth = 0.3;
            ctx.stroke();
          }
        });
      });
    };

    const animate = (time: number) => {
      if (time - lastFrameTime > 30) {
        draw(time);
        lastFrameTime = time;
      }
      animationFrameId = requestAnimationFrame(animate);
    };

    const handleResize = () => {
      const newGrid = updateGrid();
      grid = newGrid.grid;
      mouse = newGrid.mouse;
    };

    window.addEventListener('resize', handleResize);
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          animate(0);
        } else {
          cancelAnimationFrame(animationFrameId);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(canvas);

    return () => {
      window.removeEventListener('resize', handleResize);
      observer.disconnect();
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return { canvasRef };
};
